import { SplashScreen } from "@capacitor/splash-screen";
import { isAxiosError } from "axios";
import { useEffect } from "react";
import {
  isRouteErrorResponse,
  useNavigate,
  useRouteError,
} from "react-router-dom";

import { Icon } from "atoms/icon/Icon";
import Left from "atoms/icon/icons/chevron_left.svg";
import PageError from "atoms/icon/icons/page_error.svg";
import { TextButton } from "atoms/text-button/TextButton";
import useHistory from "hooks/useHistory";
import { isDevEnvironment } from "utils/environment.helpers";
import { useLogout } from "utils/useLogout";

const RootError = () => {
  const error = useRouteError();
  const { routeBackNumber } = useHistory();
  const navigate = useNavigate();
  const logout = useLogout();

  useEffect(() => {
    SplashScreen.hide();
  }, []);

  useEffect(() => {
    if (isAxiosError(error) && error.response && error.request) {
      console.error(
        `The request to ${error.request.responseURL} failed with error code ${error.response.status}`,
        error.toJSON()
      );
    } else {
      console.error("Page error occurred:", error);
    }
  }, [error]);

  return (
    <>
      <div className="flex h-screen flex-auto flex-col items-center justify-center gap-10">
        <div className="flex flex-col items-center gap-5">
          <Icon svg={PageError} color="darkGrey" size="medium" />
          <div className="text-body1 text-primary">
            Sorry, something went wrong!
          </div>
        </div>
        <TextButton
          startIconSVG={Left}
          onClick={() => navigate(routeBackNumber)}
        >
          Go Back
        </TextButton>
        {isDevEnvironment() && (
          <div className="w-full max-w-2xl space-y-4 text-left">
            URL: {location.href}
            {isRouteErrorResponse(error) ? (
              <div className="border-red-300 bg-red-50 rounded-md border p-4">
                <div className="text-red-700 mb-2 text-sm font-semibold">
                  Router error response
                </div>
                <div className="text-red-700 text-sm">
                  Status: {error.status} {error.statusText}
                </div>
                {error.data && (
                  <details className="mt-2">
                    <summary className="text-red-600 cursor-pointer">
                      Response data
                    </summary>
                    <pre className="text-red-800 mt-2 overflow-auto whitespace-pre-wrap break-words text-xs">
                      {typeof error.data === "string"
                        ? error.data
                        : JSON.stringify(error.data, null, 2)}
                    </pre>
                  </details>
                )}
                <div className="text-red-700 mt-2 text-xs">
                  URL: {location.href}
                </div>
              </div>
            ) : isAxiosError(error) ? (
              <div className="border-red-300 bg-red-50 rounded-md border p-4">
                <div className="text-red-700 mb-2 text-sm font-semibold">
                  Axios error
                </div>
                <div className="text-red-700 overflow-auto text-sm">
                  Message: {error.message}
                </div>
                {error.config && (
                  <div className="text-red-700 text-sm">
                    Request: {error.config.method?.toUpperCase()}{" "}
                    <p className="text-red-700 overflow-auto text-sm">
                      {String(error.config.url)}
                    </p>
                  </div>
                )}
                {error.response && (
                  <div className="text-red-700 text-sm">
                    Status: {error.response.status} {error.response.statusText}
                  </div>
                )}
                {error.response?.data && (
                  <details className="mt-2">
                    <summary className="text-red-600 cursor-pointer">
                      Response body
                    </summary>
                    <pre className="text-red-800 mt-2 overflow-auto whitespace-pre-wrap break-words text-xs">
                      {typeof error.response.data === "string"
                        ? error.response.data
                        : JSON.stringify(error.response.data, null, 2)}
                    </pre>
                  </details>
                )}
                {error.stack && (
                  <details className="mt-2">
                    <summary className="text-red-600 cursor-pointer">
                      Stack trace
                    </summary>
                    <pre className="text-red-800 mt-2 overflow-auto whitespace-pre text-xs">
                      {error.stack}
                    </pre>
                  </details>
                )}
              </div>
            ) : error instanceof Error ? (
              <div className="border-red-300 bg-red-50 rounded-md border p-4">
                <div className="text-red-700 mb-2 text-sm font-semibold">
                  Unhandled error
                </div>
                <div className="text-red-700 text-sm">
                  {error.name}: {error.message}
                </div>
                {error.stack && (
                  <details className="mt-2">
                    <summary className="text-red-600 cursor-pointer">
                      Stack trace
                    </summary>
                    <pre className="text-red-800 mt-2 overflow-auto whitespace-pre text-xs">
                      {error.stack}
                    </pre>
                  </details>
                )}
              </div>
            ) : (
              <div className="border-red-300 bg-red-50 rounded-md border p-4">
                <div className="text-red-700 mb-2 text-sm font-semibold">
                  Unknown error value
                </div>
                <pre className="text-red-800 overflow-auto whitespace-pre-wrap break-words text-xs">
                  {JSON.stringify(error, null, 2)}
                </pre>
              </div>
            )}
            <div className="flex gap-3">
              <TextButton onClick={() => location.reload()}>Reload</TextButton>
            </div>
          </div>
        )}

        <TextButton
          onClick={async () => {
            await logout();
            location.reload();
          }}
        >
          Logout
        </TextButton>
      </div>
    </>
  );
};

export default RootError;
